import { Injectable } from '@angular/core';
import { HttpHeaders, HttpResponse } from '@angular/common/http';

import { ServiceResponse } from "@common/data-models/service/service-response";
import { ServiceRecord } from '@common/data-models/service/service-record';
import { ServiceCollectionRecord } from '@common/data-models/service/service-collection-record';
import { FilterDef } from "@common/data-models/navigation/filter-def";
import { ObjectTypes } from "@common/enums/object-types";
import { ViewStateFilter } from "@common/data-models/navigation/viewstate-filter";
import { ViewStateRecordsAlias } from "@common/services/layout-state";
import { PSTFieldNames } from "@common/data-models/fields/pst-field-names";
import { DataStore } from "@common/data-models/data-store/data-store";
import { SingleViewStateRecord } from "@common/data-models/records/single-viewstate-record";
import { SearchViewStateRecord } from "@common/data-models/records/search-viewstate-record";
import { ViewStateTypes } from "@common/enums/viewstate-types";

import { TopicRecord } from "@playbook/data-models/records/topic-record";
import { AssignmentRecord } from "@playbook/data-models/records/assignment-record";
import { TopicTemplateRecord } from "@playbook/data-models/records/topic-template-record";
import { PlaybookSearchResults } from "@playbook/data-models/playbook-search-results/playbook-search-results";
import { ViewStateOptions } from "@suite/common/data-models/navigation/viewstate-options";

import { NomWebServicesJson } from "@common/services/nom-web-services-json";
import { GlobalTablesService } from "@common/services/global-tables-service";
import { VisualStateService } from "@common/services/visual-state-service";
import { ZFields } from "@common/data-models/fields/z-fields";

import { PlatformConvertor } from "@common/services/platform-convertor";
import { RecordFactory } from '@suite/common/data-models/records/record-factory';
import { DataType } from '@common/enums/data-type';
import { DateTime } from "@common/data-models/complex-types/date-time";
import { Util } from "@common/utils/util";
import { ContactRecord } from '@suite/common/data-models/records/contact-record';

@Injectable({ providedIn: 'root' })
export class PlaybookSearchService {
    constructor(
        public nomWebServicesJson: NomWebServicesJson,
        public globalTablesService: GlobalTablesService,
        public visualStateService: VisualStateService,
        public platformConvertor: PlatformConvertor
    ) {
    }

    public checkForTypePrivs(objectType: ObjectTypes): string {
        let typesArray = [];
        if (objectType == ObjectTypes.TOPIC || objectType == ObjectTypes.UNASSIGNED) {
            typesArray.push(ObjectTypes.TOPIC);
        }
        if (objectType == ObjectTypes.ASSIGNMENT || objectType == ObjectTypes.UNASSIGNED) {
            typesArray.push(ObjectTypes.ASSIGNMENT);
        }
        return typesArray.join(";");
    }

    public buildTemplateServiceParams(
        filter: ViewStateFilter,
        applyPrivs: boolean,
        sortBy: any,
        objectType?: number,
        search?: string,
        poller?: boolean,
        groupTemplatesLocation?: string
    ): any {
        let QueryTerms: string = "";
        if (search) {
            QueryTerms = search;
        }

        filter = (filter) ? filter.deepCopy() : new ViewStateFilter();

        let zFields = new ZFields(!this.platformConvertor.useFor["search"]);
        filter.groups = [];
        if (groupTemplatesLocation) {
            filter.groups.push(FilterDef.create("virtualPath", "TEXT", "\\\\" + this.nomWebServicesJson.PSTServer + "\\\\P_" + groupTemplatesLocation + "\\\\L"));
        }
        //get enterprise templates
        filter.groups.push(FilterDef.create("virtualPath", "TEXT", "\\\\" + this.nomWebServicesJson.PSTServer + "\\\\P_" + this.nomWebServicesJson.PSTGroup + "\\\\L"));
        //get personal templates
        filter.groups.push(FilterDef.create("virtualPath", "TEXT", "\\\\" + this.nomWebServicesJson.PSTServer + "\\\\U_" + this.nomWebServicesJson.UserId + "\\\\L"));

        if (applyPrivs) {
            if (objectType) {
                let type = this.checkForTypePrivs(objectType)
                zFields.add("type", type);
            } else {
                let type = this.checkForTypePrivs(ObjectTypes.UNASSIGNED)
                zFields.add("type__list__", type);
            }
        } else {
            if (objectType) {
                zFields.add("type", objectType);
            }
        }

        this.fillZFieldsFromFilter(zFields, filter);

        let nomLocations = [{
            BasePath: this.nomWebServicesJson.PSTServer + "\\",
            SearchWIP: true,
            SearchArchives: true,
            SearchTrash: false
        }];

        return ({
            Database: this.nomWebServicesJson.PSTServer,
            QueryTerms: "",
            zFields: zFields.data,
            NOMLocations: nomLocations,
            NOMContentTypes: {
                All: true,
                Audio: false,
                Contacts: false,
                Grids: false,
                Messages: false,
                NewsgatheringGrids: false,
                NewsgatheringItems: false,
                Pictures: false,
                Readins: false,
                Rundowns: false,
                Scripts: false,
                Storyboards: false,
                Video: false
            },
            NOMContentDates: {
                All: true,
                CustomDateRange: false,
                FortyEightHours: false,
                Future: false,
                ItemDateFrom: "1800-02-28T22:00:00.000Z",
                ItemDateTo: "1800-02-28T22:00:00.000Z",
                OneHour: false,
                OneWeek: false,
                TwentyFourHours: false,
                TwoHours: false
            },
            WireSearchParameters: {
                SearchWires: false,
                Categories: [],
                Providers: [],
                DateOptions: {
                    All: true,
                    CustomDateRange: false,
                    FortyEightHours: false,
                    Future: false,
                    ItemDateFrom: "1800-02-28T22:00:00.000Z",
                    ItemDateTo: "1800-02-28T22:00:00.000Z",
                    OneHour: false,
                    OneWeek: false,
                    TwentyFourHours: false,
                    TwoHours: false
                },
                ExtendedWireBriefing: true
            },
            MaxRows: this.visualStateService.ConfigSettings["MaxRows"],
            SortByRank: false,
            SortFields: null,
            ExactMatch: false
        });
    }
    public topicTemplates: Array<TopicTemplateRecord> = [];

    public async getTemplateSearchResults(vs: ViewStateRecordsAlias, groupTemplatesPath?: string): Promise<ServiceResponse<PlaybookSearchResults>> {
        let results = new PlaybookSearchResults();
        let response: HttpResponse<any> = null;

        let singleViewState = <SingleViewStateRecord>vs;
        let searchViewState = <SearchViewStateRecord>vs;
        let topicRequestParams = this.buildTemplateServiceParams(null, true, singleViewState.viewStateSorting, ObjectTypes.TOPIC, searchViewState.viewStateSearchQuery, null, groupTemplatesPath);
        topicRequestParams.IncludeChildren = true;
        topicRequestParams["FieldsToExclude"] = ["PSTViewStateScreenshot", "backfields"];
        try {
            response = await this.nomWebServicesJson.post(
                "/api/v2/Search",
                topicRequestParams,
                {
                    headers: new HttpHeaders({
                        "Content-Type": "application/json",
                        "X-ENPS-TOKEN": this.nomWebServicesJson.NomTokenId
                    }),
                    responseType: 'json',
                    observe: 'response'
                }
            );
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: "Failed to load search results!"
            });
            return new ServiceResponse<PlaybookSearchResults>(true, results);
        }
        let assignmentParentGuids: { [id: string]: Array<string> } = {}; // parentGuid to guid map
        if (response) {
            let i = 0;
            let respObj = response.body;
            for (let p of respObj.SearchResults) {
                i++;
                if (p.ListData.Type == 137) {
                    //1. add Topic results
                    let serviceCollectionRecord: ServiceCollectionRecord = ServiceCollectionRecord.instance(p);
                    let topicTemplateRecord = this.nomWebServicesJson.Convertor.fromServiceCollectionTemplateRecord<TopicTemplateRecord>(serviceCollectionRecord);

                    let record = topicTemplateRecord;
                    record.setChildren([]);
                    results.addTemplate(record); //add the templates
                    for (let childScript of p.CollectionScripts) {
                        let assignmentRecord = this.nomWebServicesJson.Convertor.fromServiceRecord<AssignmentRecord>(childScript);
                        let assignment = assignmentRecord;
                        if (record && !record.hasChildWithGuid(assignment.guid)) {
                            record.unshiftChildren(assignment);
                        }
                    }
                }
                if (p.ListData.Type == 138) {
                    //2. assignment results - that may or may not have topics
                    let serviceRecord: ServiceRecord = ServiceRecord.instance(p);
                    let assignmentRecord = this.nomWebServicesJson.Convertor.fromServiceRecord<AssignmentRecord>(serviceRecord);
                    results.addAssignment(assignmentRecord);
                    if (!assignmentParentGuids[assignmentRecord.parentGuid]) assignmentParentGuids[assignmentRecord.parentGuid] = [];
                    assignmentParentGuids[assignmentRecord.parentGuid].push(assignmentRecord.guid);
                }
            }
        }
        this.topicTemplates = results.templates;
        results.objectType = ObjectTypes.TOPIC;
        return new ServiceResponse<PlaybookSearchResults>(true, results);
    }

    protected buildFilterServiceParams(filter: ViewStateFilter, applyPrivs: boolean, sortBy: any, sortOrder: string, objectType?: number, search?: string, poller?: boolean, guid?: string): any {
        let QueryTerms: string = "";
        if (search) {
            QueryTerms = search;
        }

        filter = (filter) ? filter.deepCopy() : new ViewStateFilter();

        let zFields = new ZFields(!this.platformConvertor.useFor["search"]);
        zFields.add(
            "virtualPath",
            "\\\\" + this.nomWebServicesJson.PSTServer +
            "\\\\P_" + this.nomWebServicesJson.PSTGroup +
            "\\\\W\\\\" + this.nomWebServicesJson.PSTHoldFolder
        );
        if (applyPrivs) {
            if (guid) {
                let type = this.checkForTypePrivs(ObjectTypes.UNASSIGNED)
                zFields.add("type__list__", type);
            } else {
                if (objectType) {
                    let type = this.checkForTypePrivs(objectType)
                    zFields.add("type", type);
                } else {
                    let type = this.checkForTypePrivs(ObjectTypes.UNASSIGNED)
                    zFields.add("type__list__", type);
                }
            }
        } else {
            if (objectType) {
                zFields.add("type", objectType);
            }
        }

        let queryTermTags: Array<string> = [];
        let queryTermUsers: Array<string> = [];
        let queryTermWords: Array<string> = [];

        let queryTermsArr = [];

        if (QueryTerms.startsWith("#")) {
            queryTermTags.push(QueryTerms.substr(1));
        } else { //we should support search for tags which consists of more than one word 
            queryTermsArr = QueryTerms.split(" ");
        }
        for (let term of queryTermsArr) {
            if (term.startsWith("@")) {
                queryTermUsers.push(term.substr(1));
            } else {
                queryTermWords.push(term);
            }
        }

        if (queryTermTags.length > 0) {
            let values: Array<string> = [];
            if (filter.map[PSTFieldNames.PSTTopicTag] && filter.map[PSTFieldNames.PSTTopicTag].Value && Array.isArray(filter.map[PSTFieldNames.PSTTopicTag].Value)) {
                for (let v of (<Array<string>>filter.map[PSTFieldNames.PSTTopicTag].Value)) {
                    if (values.indexOf(v) < 0) {
                        values.push(v);
                    }
                }
            }
            if (queryTermTags.length > 0) {
                for (let termTag of queryTermTags) {
                    if (values.indexOf(termTag) < 0) {
                        values.push(termTag);
                    }
                }
            }
            if (values.length > 0) {
                filter.push(FilterDef.create(PSTFieldNames.PSTTopicTag, "LOOKUPLIST", values));
            }
        }

        if (queryTermUsers.length > 0) {
            for (let field of this.globalTablesService.FieldDefs) {
                let values: Array<string> = [];
                let isStaffIDList = this.globalTablesService.isStaffIDListFieldType(field.ID);
                if (isStaffIDList) {
                    if (queryTermUsers.length > 0) {
                        for (let termUser of queryTermUsers) {
                            let staffIDFilter = new FilterDef();
                            filter.groups.push(FilterDef.create(field.ID, "STAFFIDLIST", termUser));
                        }
                    }
                }
            }
        }
        this.fillZFieldsFromFilter(zFields, filter, guid);
        let sortFields = []
        if (!guid) {
            sortFields = this.buildSortFields(objectType, sortBy, sortOrder);
        }

        return ({
            Database: this.nomWebServicesJson.PSTServer,
            QueryTerms: (queryTermWords.length == 0) ? "" : queryTermWords.join(" "),
            zFields: zFields.data,
            NOMLocations: [{
                BasePath: "P_" + this.nomWebServicesJson.PSTGroup + "\\",
                SearchWIP: true,
                SearchArchives: true,
                SearchTrash: false
            }],
            NOMContentTypes: {
                All: true,
                Audio: false,
                Contacts: false,
                Grids: false,
                Messages: false,
                NewsgatheringGrids: false,
                NewsgatheringItems: false,
                Pictures: false,
                Readins: false,
                Rundowns: false,
                Scripts: false,
                Storyboards: false,
                Video: false
            },
            NOMContentDates: {
                All: true,
                CustomDateRange: false,
                FortyEightHours: false,
                Future: false,
                ItemDateFrom: "1800-02-28T22:00:00.000Z",
                ItemDateTo: "1800-02-28T22:00:00.000Z",
                OneHour: false,
                OneWeek: false,
                TwentyFourHours: false,
                TwoHours: false
            },
            WireSearchParameters: {
                SearchWires: false,
                Categories: [],
                Providers: [],
                DateOptions: {
                    All: true,
                    CustomDateRange: false,
                    FortyEightHours: false,
                    Future: false,
                    ItemDateFrom: "1800-02-28T22:00:00.000Z",
                    ItemDateTo: "1800-02-28T22:00:00.000Z",
                    OneHour: false,
                    OneWeek: false,
                    TwentyFourHours: false,
                    TwoHours: false
                },
                ExtendedWireBriefing: true
            },
            // MaxRows: maxResults,
            MaxRows: this.visualStateService.ConfigSettings["MaxRows"],
            SortByRank: false,
            SortFields: sortFields,
            ExactMatch: false
        });
    }

    public async getFilteredResultsGuids(vs: ViewStateRecordsAlias): Promise<ServiceResponse<Array<{ [id: string]: any }>>> {
        let ret: Array<{ [id: string]: any }> = [];

        let objectType = this.getObjectTypeByViewStateType(vs.viewStateType);
        let singleViewState = <SingleViewStateRecord>vs;
        let searchViewState = <SearchViewStateRecord>vs;
        let requestParams = this.buildFilterServiceParams(singleViewState.viewStateFilter, true, { Value: "CreatedDateTime" }, "desc", objectType, searchViewState.viewStateSearchQuery, false);

        requestParams.FieldsToReturn = ["guid", "type", PSTFieldNames.PSTTopicDepartment, "CreatedDateTime"];
        let response: HttpResponse<any> = null;
        try {
            response = await this.nomWebServicesJson.post(
                "/api/SearchFields",
                requestParams,
                {
                    headers: new HttpHeaders({
                        "Content-Type": "application/json",
                        "X-ENPS-TOKEN": this.nomWebServicesJson.NomTokenId
                    }),
                    responseType: 'json',
                    observe: 'response'
                }
            );
        } catch (error) {
            Util.saveWarningError("Failed to load filtered results! StackTrace: NomWebServicesJson.getFilteredResultsGuids(); Error:" + error.toString());
            return new ServiceResponse<Array<{ [id: string]: any }>>(false, ret);
        }

        let i = 0;

        if (response) {
            let respObj = response.body;
            for (let p of respObj.SearchResults) {
                let obj: { [id: string]: any } = {};
                for (let item of p) {
                    obj[item.FieldName] = item.FieldValue;
                }
                ret.push(obj);
            }
            return new ServiceResponse<Array<{ [id: string]: any }>>(true, ret);
        } else {
            return new ServiceResponse<Array<{ [id: string]: any }>>(false, "Failed to load updated guid list from web service");
        }
    }

    private getObjectTypeByViewStateType(vsType: ViewStateTypes, guid?: string) {
        if (vsType == ViewStateTypes.TOPIC || vsType == ViewStateTypes.SINGLETOPIC) {
            return ObjectTypes.TOPIC;
        }
        if (vsType == ViewStateTypes.ASSIGNMENT) {
            return ObjectTypes.ASSIGNMENT;
        }
        if (guid) {
            return ObjectTypes.UNASSIGNED;
        }
        return null;
    }

    public async getSearchResults(vs: ViewStateRecordsAlias, guid?: string): Promise<ServiceResponse<PlaybookSearchResults>> {
        // TODO: Add topic type parameters
        let results = new PlaybookSearchResults();
        let response: HttpResponse<any> = null;

        let objectType = this.getObjectTypeByViewStateType(vs.viewStateType, guid);       
        let singleViewState = <SingleViewStateRecord>vs;
        let searchViewState = <SearchViewStateRecord>vs;

        let topicRequestParams = this.buildFilterServiceParams(singleViewState.viewStateFilter, true, singleViewState.viewStateSorting, singleViewState.viewStateSortOrder, objectType, searchViewState.viewStateSearchQuery, false, guid);
        topicRequestParams.IncludeChildren = true;
        topicRequestParams.IncludeLinkedRecords = true;
        topicRequestParams["FieldsToExclude"] = ["PSTViewStateScreenshot", "backfields"];
        try {
            response = await this.nomWebServicesJson.post(
                "/api/v2/Search",
                topicRequestParams,
                {
                    headers: new HttpHeaders({
                        "Content-Type": "application/json",
                        "X-ENPS-TOKEN": this.nomWebServicesJson.NomTokenId
                    }),
                    responseType: 'json',
                    observe: 'response'
                });
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: "Failed to load search results!",
                detailMessage: `Failed to load search results! \n\rStackTrace: NomWebServicesJson.getSearchResults(); \n\rError: ${error}`
            });
            return new ServiceResponse<PlaybookSearchResults>(false, results);
        }
        let assignmentParentGuids: { [id: string]: Array<string> } = {}; // parentGuid to guid map
        if (response) {
            results.searchTimestamp = DataType.fromRaw(response.headers.get("timestamp"), DataType.DATE) as DateTime;
            let respObj = response.body;
            for (let p of respObj.SearchResults) {
                if (p.ListData.Type == 137) {
                    //1. add Topic results
                    let serviceCollectionRecord: ServiceCollectionRecord = ServiceCollectionRecord.instance(p);
                    let topicRecord = this.nomWebServicesJson.Convertor.fromServiceCollectionRecord<TopicRecord>(serviceCollectionRecord);
                    await DataStore.setCollection(topicRecord, true);
                    let record = DataStore.byGuid<TopicRecord>(topicRecord.guid, "");
                    record.setChildren([]);
                    results.addTopic(record);
                    for (let childScript of p.CollectionScripts) {
                        let assignmentRecord = this.nomWebServicesJson.Convertor.fromServiceRecord<AssignmentRecord>(childScript);
                        DataStore.set(assignmentRecord);
                        let assignment = DataStore.byGuid<AssignmentRecord>(assignmentRecord.guid, "");
                        if (record && !record.hasChildWithGuid(assignment.guid)) {
                            record.unshiftChildren(assignment);
                        } else {
                            record.registerChildHooks(assignment);
                        }
                    }
                    record.links = [];
                    for (let linkedRecord of serviceCollectionRecord.LinkedRecords) {
                        if (linkedRecord.ListData.Type == ObjectTypes.CONTACT) {
                            let serviceRecord: ServiceRecord = ServiceRecord.instance(linkedRecord);
                            let contactRecord = this.nomWebServicesJson.Convertor.fromServiceRecord<ContactRecord>(serviceRecord);
                            DataStore.set(contactRecord);
                            record.links.push(contactRecord)
                        }
                    }
                }
                if (p.ListData.Type == 138) {
                    //2. assignment results - that may or may not have topics
                    let serviceRecord: ServiceRecord = ServiceRecord.instance(p);
                    let assignmentRecord = this.nomWebServicesJson.Convertor.fromServiceRecord<AssignmentRecord>(serviceRecord);
                    let parentRec: TopicRecord = DataStore.byGuid<TopicRecord>(assignmentRecord.parentGuid, "");
                    if (!parentRec) {
                        parentRec = RecordFactory.createRecordForType<TopicRecord>(ObjectTypes.TOPIC)
                    }
                    parentRec.guid = assignmentRecord.parentGuid;
                    parentRec.categories = assignmentRecord.parentCategory;
                    parentRec.topicType = assignmentRecord.parentType;
                    parentRec.storyOwner = assignmentRecord.parentOwner;
                    parentRec.location = assignmentRecord.parentLocation;
                    parentRec.title = p.ListData.ContainerTitle;
                    await DataStore.setCollection(parentRec, true);
                    if (parentRec && !parentRec.hasChildWithGuid(assignmentRecord.guid)) {
                        parentRec.unshiftChildren(assignmentRecord);
                    } else {
                        parentRec.registerChildHooks(assignmentRecord);
                    }
                    DataStore.set(assignmentRecord);
                    results.addAssignment(DataStore.byGuid<AssignmentRecord>(assignmentRecord.guid, ""));
                    if (!assignmentParentGuids[assignmentRecord.parentGuid]) assignmentParentGuids[assignmentRecord.parentGuid] = [];
                    assignmentParentGuids[assignmentRecord.parentGuid].push(assignmentRecord.guid);
                }
            }
        }
        results.objectType = objectType || ObjectTypes._SEARCH;
        return new ServiceResponse<PlaybookSearchResults>(true, results);
    }

    protected buildSortFields(objectType: any, sortByObj: ViewStateOptions, sortOrder: string): Array<{ [id: string]: string }> {
        let sortBy = sortByObj.Value;
        if (sortBy) {
            sortBy = sortBy.replace(/"/g, ""); // strip quotes before applying to search query
            sortBy = sortBy.replace(/(-.*)/g, ""); // replace -date -time parts
        }
        if (this.platformConvertor.useFor['search']) {
            if (sortBy == PSTFieldNames.PSTModTime) sortBy = "modTime";
            return [{ FieldName: sortBy, SortOrder: sortOrder }, { FieldName: 'guid', SortOrder: "asc" }]
        } else {
            if (sortBy == PSTFieldNames.PSTModTime) sortBy = "modTime";
            return [{ FieldName: sortBy, FieldValue: sortOrder }, { FieldName: 'guid', FieldValue: "asc" }]
        }
    }


    public async getNewItems(guids: Array<string>): Promise<Array<TopicRecord>> {
        let topicRequestParams: any = {
            Database: this.nomWebServicesJson.PSTServer,
            QueryTerms: "",
            zFields: [{ FieldName: "guid__list__", FieldValue: guids.join(";") }],
            NOMLocations: [{
                BasePath: "P_" + this.nomWebServicesJson.PSTGroup + "\\",
                SearchWIP: true,
                SearchArchives: true,
                SearchTrash: false
            }],
            NOMContentTypes: {
                All: true,
                Audio: false,
                Contacts: false,
                Grids: false,
                Messages: false,
                NewsgatheringGrids: false,
                NewsgatheringItems: false,
                Pictures: false,
                Readins: false,
                Rundowns: false,
                Scripts: false,
                Storyboards: false,
                Video: false
            },
            NOMContentDates: {
                All: true,
                CustomDateRange: false,
                FortyEightHours: false,
                Future: false,
                ItemDateFrom: "1800-02-28T22:00:00.000Z",
                ItemDateTo: "1800-02-28T22:00:00.000Z",
                OneHour: false,
                OneWeek: false,
                TwentyFourHours: false,
                TwoHours: false
            },
            WireSearchParameters: {
                SearchWires: false,
                Categories: [],
                Providers: [],
                DateOptions: {
                    All: true,
                    CustomDateRange: false,
                    FortyEightHours: false,
                    Future: false,
                    ItemDateFrom: "1800-02-28T22:00:00.000Z",
                    ItemDateTo: "1800-02-28T22:00:00.000Z",
                    OneHour: false,
                    OneWeek: false,
                    TwentyFourHours: false,
                    TwoHours: false
                },
                ExtendedWireBriefing: true
            },
            MaxRows: 200,
            SortByRank: false,
            SortFields: {},
            ExactMatch: false
        };
        let response: HttpResponse<any> = null;
        let topics: Array<TopicRecord> = new Array<TopicRecord>();
        topicRequestParams.IncludeChildren = true;
        topicRequestParams["FieldsToExclude"] = ["PSTViewStateScreenshot", "backfields"];
        let assignmentParentGuids: { [id: string]: Array<string> } = {}; // parentGuid to guid map
        try {
            response = await this.nomWebServicesJson.post(
                "/api/v2/Search",
                topicRequestParams,
                {
                    headers: new HttpHeaders({
                        "Content-Type": "application/json",
                        "X-ENPS-TOKEN": this.nomWebServicesJson.NomTokenId
                    }),
                    responseType: 'json',
                    observe: 'response'
                });
            let respObj = response.body;
            for (let p of respObj.SearchResults) {
                if (p.ListData.Type == 137) {
                    //1. add Topic results
                    let serviceCollectionRecord: ServiceCollectionRecord = ServiceCollectionRecord.instance(p);
                    let topicRecord = this.nomWebServicesJson.Convertor.fromServiceCollectionRecord<TopicRecord>(serviceCollectionRecord);
                    await DataStore.setCollection(topicRecord, true);
                    let record = DataStore.byGuid<TopicRecord>(topicRecord.guid, "");
                    record.setChildren([]);
                    topics.push(record);
                    for (let childScript of p.CollectionScripts) {
                        let assignmentRecord = this.nomWebServicesJson.Convertor.fromServiceRecord<AssignmentRecord>(childScript);
                        DataStore.set(assignmentRecord);
                        let assignment = DataStore.byGuid<AssignmentRecord>(assignmentRecord.guid, "");
                        if (record && !record.hasChildWithGuid(assignment.guid)) {
                            record.unshiftChildren(assignment);
                        }
                    }
                }
                if (p.ListData.Type == 138) {
                    //2. assignment results - that may or may not have topics
                    let serviceRecord: ServiceRecord = ServiceRecord.instance(p);
                    let assignmentRecord = this.nomWebServicesJson.Convertor.fromServiceRecord<AssignmentRecord>(serviceRecord);
                    let parentRec = DataStore.byGuid<TopicRecord>(assignmentRecord.parentGuid, "");
                    if (parentRec && !parentRec.hasChildWithGuid(assignmentRecord.guid)) {
                        parentRec.unshiftChildren(assignmentRecord);
                    }
                    DataStore.set(assignmentRecord);
                    if (!assignmentParentGuids[assignmentRecord.parentGuid]) assignmentParentGuids[assignmentRecord.parentGuid] = [];
                    assignmentParentGuids[assignmentRecord.parentGuid].push(assignmentRecord.guid);
                }
            }


        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: "Failed to load new items!",
                detailMessage: `Failed to load new items! \n\rStackTrace: NomWebServicesJson.getNewItems(); \n\rError: ${error}`
            });
        }
        return topics;
    }

    private resolveFieldName(name: string): string {
        if (!name || typeof (name) !== "string") {
            return name;
        }
        return name.startsWith("ModTime") ? "modTime__range__" : name;
    }

    private getOwnersFilter(filterObj: ViewStateFilter): Array<string> {
        let filtersObj = filterObj.getFilters(ViewStateTypes.TOPIC)
        let pstLocationFilter = filtersObj.filter(f => f.ID === "PSTTeamLocation")[0]
        let pstTopicOwnerFilter = filtersObj.filter(f => f.ID === "PSTTopicOwner")[0]
        let filteredLocations = [];
        let filteredOwners = [];
        if (pstLocationFilter && !pstLocationFilter.NotUsed && !pstLocationFilter.IsAllSelected) {
            filteredLocations = pstLocationFilter.Value;
            if (pstTopicOwnerFilter && !pstTopicOwnerFilter.NotUsed && Object.keys(pstTopicOwnerFilter.Value.data).length > 0) {
                filteredOwners = Object.keys(this.globalTablesService.Staff).filter(s => filteredLocations.indexOf(this.globalTablesService.Staff[s].Program) > -1 && pstTopicOwnerFilter.Value.data[this.globalTablesService.Staff[s].UserID]);
                if (filteredOwners.length === 0) {
                    filteredOwners.push('@no_owners');
                }
            } else {
                filteredOwners = Object.keys(this.globalTablesService.Staff).filter(s => filteredLocations.indexOf(this.globalTablesService.Staff[s].Program) > -1);
                pstTopicOwnerFilter.NotUsed = false;
            }
            pstLocationFilter.NotUsed = true;
        }
        return filteredOwners;
    }

    protected fillZFieldsFromFilter(zFields: ZFields, filterObj: ViewStateFilter, guid?: string) {
        if (filterObj && !guid) {
            for (let group of filterObj.groups) {
                let value = group.group();
                if (value) {
                    for (let g of value) {
                        zFields.add(this.resolveFieldName(g.FieldName), g.FieldValue);
                    }
                }
            }
            let filteredOwners = this.getOwnersFilter(filterObj);
            for (let filter of filterObj.getFilters(ViewStateTypes.TOPIC)) {
                if (filter.NotUsed) { // neither in the required nor in the optional list
                    continue;
                }
                let value = filter.get(filteredOwners);

                if (value) {
                    zFields.add(this.resolveFieldName(value.FieldName), value.FieldValue);
                }
            }
        }
        if (guid) {
            zFields.add("guid", guid);
        }

        return zFields;
    }

}
